// packages/core/src/learning/school-modules/fintech-automation.ts
// ─────────────────────────────────────────────────────────────────────────
// LENS PACK — "Fintech & Automation" (surface: 'school').
//
// Conforms to LearningModule (LearningTypes.ts) and passes _validateLensPacks:
//   - surface: 'school', homeSchool: 'fintech-automation', no assessment
//   - every lesson lessonKind: 'school-core', exactly 3 role-tagged flashcards
//   - exactly one applyAction per lesson, all live + allowed on 'lens-lesson'
//     (noToolAction ×2, openDashboard ×1)
//   - lesson ids namespaced `fa-*`, flashcard ids `fa-*-fc-*` (globally unique)
//   - concepts non-empty, no intra-pack duplicates
//
// Module id / homeSchool match the locked naming convention so the school's
// exhibit nativeModuleId: 'school-fintech-automation' resolves.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_FINTECH_AUTOMATION_MODULE: LearningModule = {
	id: 'school-fintech-automation',
	title: 'Fintech & Automation',
	description:
		'The worldview that technology should make good money habits easier to repeat. Learn to treat automation as a guardrail rather than a brain replacement, use dashboards to turn drift into decisions, and keep convenience from making spending invisible.',
	emoji: '⚙️',
	accentColor: '#c8ff00',
	tier: 'control',
	pillar: 'cash-flow-control',
	order: 1,
	surface: 'school',
	homeSchool: 'fintech-automation',
	lessons: [
		// ── Lesson 1 ─────────────────────────────────────────────────────
		{
			id: 'fa-automation-removes-friction',
			title: 'Automation removes friction, not responsibility',
			description:
				'This lesson teaches why the fintech automation lens uses apps and auto pay to reduce repeated decisions, while still keeping the user responsible for the system.',
			pillar: 'cash-flow-control',
			schoolsOfThought: ['fintech-automation'],
			estimatedReadMinutes: 2,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['automation', 'auto-pay', 'guardrails'],
			prerequisites: [],
			relatedLessons: [
				'fa-dashboards-reveal-drift',
				'fa-convenience-hides-cost',
			],
			lessonKind: 'school-core',
			concepts: [
				'automation as guardrail',
				'friction reduction',
				'responsibility stays human',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Money mistakes often happen because normal life is noisy. Bills arrive on different days, subscriptions renew quietly, transfers get delayed, and good intentions disappear when the week gets busy.',
				},
				{
					type: 'paragraph',
					text: 'The fintech automation lens believes good systems should make the right behavior easier to repeat. Automation can help boring money habits happen without requiring motivation every time.',
				},
				{
					type: 'bullet_list',
					items: [
						'Auto pay reduces missed bills',
						'Scheduled transfers reduce decision fatigue',
						'Alerts make hidden changes visible',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is thinking automation means you can stop paying attention. Automation can run the system, but it cannot care about the outcome.',
				},
				{
					type: 'example',
					text: 'Someone sets an automatic transfer to savings every payday. That removes the need to remember, but they still need to check whether the transfer fits rent, food, debt payments, and real cash flow.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that automation can create false confidence. A system that once worked can become wrong when income changes, bills rise, a card expires, or a subscription keeps charging.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens turns automation into a review habit. The goal is not more apps. The goal is fewer repeated decisions and clearer control.',
				},
				{
					type: 'key_takeaway',
					text: 'Automation is a guardrail. It is not a replacement for judgment.',
				},
			],
			audioScript:
				'The fintech automation lens starts with a realistic view of human behavior. Most people do not fail with money because they lack a perfect spreadsheet. They fail because life gets busy. Bills come due when attention is somewhere else. A paycheck lands, and a dozen choices compete for it. A subscription renews. A payment date moves. A card expires. A small mistake becomes a fee, and the fee creates more stress.\n\nThis school believes technology can help by removing friction from repeated behaviors. If rent is predictable, auto pay can reduce the chance of forgetting. If you want to build savings, a scheduled transfer can move the money before the day gets crowded. If a bill rises, an alert can make the change visible. If spending is scattered across accounts, a dashboard can pull the picture together so you are not guessing.\n\nBut this lens is not saying apps solve discipline by magic. That is the trap. Automation can run a system, but it cannot understand your life. It does not know that your hours were cut. It does not know that your rent increased. It does not know that you are helping family this month. It does not know that a subscription no longer matters to you. It simply follows rules.\n\nThat is why automation works best when it is treated as a guardrail, not a brain replacement. A guardrail keeps you from drifting off the road, but you still drive. The system can reduce repeated decisions, but you still choose the direction. You still decide what matters, what gets funded first, what needs to be paused, and what no longer fits.\n\nImagine someone sets an automatic savings transfer for every payday. At first, it works. They feel proud because money is finally moving before they spend it. Then rent rises, groceries rise, and a new insurance payment starts. The transfer keeps happening, but now the checking account gets tight before the next paycheck. If they never review the system, the automation that once helped can start creating stress.\n\nThe fintech automation lens says the mature move is rhythm. Automate the repeated behavior, then review the system on purpose. The review does not have to be dramatic. It can be simple: what came in, what went out, what changed, what needs to be adjusted, and what is still aligned with the plan.\n\nThe blind spot is that this lens can make people chase apps instead of clarity. A new dashboard, card, bank feature, budget app, or alert can feel like progress. Sometimes it is. But if the system does not change behavior or improve visibility, it is just decoration.\n\nThe real win is not having the most advanced setup. The real win is having fewer ways to forget, fewer places for money to leak, and a clearer view of what your system is doing while you live your life. Automation should make discipline easier, not invisible.',
			flashcards: [
				{
					id: 'fa-automation-removes-friction-fc-main',
					role: 'main_idea',
					front: 'What does automation do best?',
					back: 'It reduces friction around repeated money habits.',
				},
				{
					id: 'fa-automation-removes-friction-fc-watch',
					role: 'watch_out',
					front: 'What is the automation trap?',
					back: 'Thinking a system can replace attention, review, or judgment.',
				},
				{
					id: 'fa-automation-removes-friction-fc-use',
					role: 'use_it',
					front: 'How should automation be used?',
					back: 'Use it as a guardrail, then review whether it still fits real life.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Design one money guardrail',
				prompt: 'Write one money habit you want to automate, then write when you will review whether it still fits.',
			},
			revision: 1,
		},

		// ── Lesson 2 ─────────────────────────────────────────────────────
		{
			id: 'fa-dashboards-reveal-drift',
			title: 'Dashboards make hidden drift visible',
			description:
				'This lesson teaches why the fintech automation lens values dashboards when they reveal drift, not when they create a false feeling of control.',
			pillar: 'cash-flow-control',
			schoolsOfThought: ['fintech-automation'],
			estimatedReadMinutes: 2,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['dashboards', 'visibility', 'spending-drift'],
			prerequisites: ['fa-automation-removes-friction'],
			relatedLessons: [
				'fa-automation-removes-friction',
				'fa-convenience-hides-cost',
			],
			lessonKind: 'school-core',
			concepts: [
				'visibility beats guessing',
				'dashboard drift',
				'numbers need review',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Money drift is quiet. A bill rises by a few dollars, eating out becomes normal, subscriptions stack up, and the month starts feeling tight without one obvious cause.',
				},
				{
					type: 'paragraph',
					text: 'The fintech automation lens believes dashboards are useful because they turn scattered money behavior into a visible pattern. The value is not the chart itself. The value is what the chart helps you notice.',
				},
				{
					type: 'bullet_list',
					items: [
						'Dashboards reveal patterns',
						'Trends show drift',
						'Categories expose quiet leaks',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating a dashboard like control. Seeing a problem is not the same as changing the behavior behind it.',
				},
				{
					type: 'example',
					text: 'A dashboard shows that food delivery went from $80 one month to $260 the next. The app did not fix the problem, but it made the pattern hard to ignore.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that dashboards can become another place to stare at numbers without acting. Too much tracking can create noise if the user never decides what the numbers mean.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects visibility to a question: what is changing without your permission? That is where dashboards become useful.',
				},
				{
					type: 'key_takeaway',
					text: 'A dashboard is valuable when it turns drift into a decision.',
				},
			],
			audioScript:
				'The fintech automation lens cares a lot about visibility because most money problems do not announce themselves all at once. They drift. A few extra delivery orders. A subscription renewal. A higher insurance bill. A small fee. A slightly larger grocery run. A payment plan that felt harmless when it started. None of these may feel huge on its own, but together they can change the shape of the month.\n\nThat is where dashboards can help. A good dashboard takes scattered behavior and makes it visible. Instead of trying to remember what happened, you can see patterns. Instead of guessing why the month felt tight, you can compare categories. Instead of blaming yourself vaguely, you can point to what changed.\n\nThis is important because vague money stress is hard to solve. If the only thought is, “I feel broke,” the brain usually reaches for shame or panic. But if the dashboard shows that subscriptions rose, food spending climbed, income dipped, and a payment hit earlier than expected, the problem becomes more concrete. Concrete problems are easier to think about. They may still be hard, but they are no longer invisible.\n\nThe anti trap rule is simple: seeing is not the same as controlling. A dashboard can show that delivery spending rose from $80 to $260. That is useful. But the dashboard does not decide what to do about it. It does not know whether that spending was stress, convenience, celebration, work schedule, family needs, or habit. The user still has to interpret the pattern.\n\nThis is where the fintech automation lens is strongest. It does not worship the chart. It asks what the chart changes. Did it reveal a leak? Did it show that a goal is not funded? Did it expose a subscription you forgot? Did it show that income is less steady than you assumed? Did it help you notice that one category is eating the space that was supposed to become savings, runway, or debt progress?\n\nThe blind spot is that dashboards can become another form of avoidance. Some people track everything and still change nothing. They refresh the numbers, reorganize categories, download new apps, connect more accounts, and feel productive without making a real decision. That is not clarity. That is number watching.\n\nA healthier dashboard habit has a purpose. You look at the numbers to answer a small set of questions. What changed? What surprised me? What is still aligned? What needs a decision? What can be ignored? This keeps the dashboard from becoming noise.\n\nInside Nisk, this lens fits the idea that money should become visible enough to test. If your current picture is blurry, every decision feels emotional. Once the picture is clearer, you can simulate changes, compare tradeoffs, and make better choices. The dashboard does not create discipline by itself, but it can show where discipline is needed.\n\nThe point is not to stare at your money all day. The point is to stop letting quiet drift make decisions for you.',
			flashcards: [
				{
					id: 'fa-dashboards-reveal-drift-fc-main',
					role: 'main_idea',
					front: 'What do dashboards reveal?',
					back: 'They reveal patterns, drift, and hidden changes in money behavior.',
				},
				{
					id: 'fa-dashboards-reveal-drift-fc-watch',
					role: 'watch_out',
					front: 'What is dashboard false control?',
					back: 'Feeling in control because you can see numbers, even when behavior has not changed.',
				},
				{
					id: 'fa-dashboards-reveal-drift-fc-use',
					role: 'use_it',
					front: 'What should a dashboard answer?',
					back: 'Ask what changed, what surprised you, and what needs a decision.',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Review your money picture',
			},
			revision: 1,
		},

		// ── Lesson 3 ─────────────────────────────────────────────────────
		{
			id: 'fa-convenience-hides-cost',
			title: 'Convenience can make spending invisible',
			description:
				'This lesson teaches why the fintech automation lens warns that smooth payments, subscriptions, and one tap buying can weaken attention.',
			pillar: 'cash-flow-control',
			schoolsOfThought: ['fintech-automation'],
			estimatedReadMinutes: 2,
			estimatedListenMinutes: 5,
			difficulty: 3,
			tags: ['convenience', 'subscriptions', 'frictionless-spending'],
			prerequisites: ['fa-automation-removes-friction'],
			relatedLessons: [
				'fa-automation-removes-friction',
				'fa-dashboards-reveal-drift',
			],
			lessonKind: 'school-core',
			concepts: [
				'convenience hides cost',
				'subscription creep',
				'smooth spending risk',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Fintech makes money move faster. That can be helpful, but it also means spending can happen with almost no friction, memory, or emotional pause.',
				},
				{
					type: 'paragraph',
					text: 'This lens believes convenience is powerful because it removes effort. The same feature that makes good habits easier can also make bad habits easier.',
				},
				{
					type: 'bullet_list',
					items: [
						'One tap buying reduces pause',
						'Subscriptions hide repetition',
						'Payment plans soften the real price',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is confusing smooth with harmless. A payment can feel small because the app made it easy, not because the cost is actually small.',
				},
				{
					type: 'example',
					text: 'A $12 subscription feels tiny. Ten forgotten subscriptions become a real monthly claim on income. The danger is not one charge. The danger is not noticing the stack.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that convenience is not bad by itself. Faster payments, auto pay, and subscriptions can save time. The risk begins when convenience removes awareness from decisions that deserve attention.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens turns convenience into a question: what spending has become automatic without still being important?',
				},
				{
					type: 'key_takeaway',
					text: 'The easier money is to spend, the more intentionally it needs to be reviewed.',
				},
			],
			audioScript:
				'The fintech automation lens has a warning that matters more every year: convenience can make spending invisible. That does not mean convenience is bad. It means convenience changes behavior. When paying is easier, buying is easier. When buying is easier, the pause gets shorter. When the pause gets shorter, the decision can happen before your values get a vote.\n\nThink about how different spending feels now compared with handing over cash. You can tap a phone, save a card, click once, split a payment, subscribe, renew automatically, or finance something at checkout. The friction is almost gone. That is useful when the payment supports something important. It is dangerous when the payment is mostly impulse, stress, boredom, or habit.\n\nThis school is paying attention to the gap between cost and feeling. A purchase can feel small because the app made it smooth. A subscription can feel harmless because it is only $9 or $12. A payment plan can feel affordable because the full price is broken into pieces. A delivery order can feel normal because the card is already saved. None of that means the cost disappeared. It only means the cost became easier to ignore.\n\nSubscription creep is the cleanest example. One subscription may be fine. But ten subscriptions, some forgotten and some barely used, become a monthly claim on income. The issue is not that every subscription is bad. The issue is that automatic renewal can outlive the original decision. A service you cared about six months ago may keep charging after it no longer matters. The system keeps moving because nobody told it to stop.\n\nPayment plans create a similar risk. A smaller monthly amount can make a larger price feel safer than it is. That does not mean every payment plan is wrong. It means the full obligation still matters. A $40 payment joins the other fixed payments. Enough of them can shrink freedom without one dramatic decision ever happening.\n\nThe fintech automation lens does not want to remove every convenience. That would be unrealistic and annoying. Faster payments, auto pay, subscriptions, and saved cards can save time and reduce friction. The goal is not to make life harder for no reason. The goal is to choose where friction belongs.\n\nSome decisions should be smooth. Paying a normal bill on time can be smooth. Moving money into savings can be smooth. Some decisions deserve a speed bump. New subscriptions, payment plans, large purchases, recurring charges, and emotional spending often deserve a pause.\n\nThe blind spot is that this lens can become too restrictive if it treats every convenience as a threat. Convenience is a design choice, not automatically a problem. The question is whether the convenience serves your life or quietly redirects it.\n\nA useful review question is: what spending has become automatic without still being important? That question is simple, but it is powerful. It turns invisible claims back into visible choices. You may keep some. You may cancel some. You may change how payments happen. The point is that your money stops moving without your attention.\n\nConvenience is strongest when it supports your priorities. It becomes dangerous when it replaces them.',
			flashcards: [
				{
					id: 'fa-convenience-hides-cost-fc-main',
					role: 'main_idea',
					front: 'Why can convenience be risky?',
					back: 'It removes friction, which can make spending happen before attention catches up.',
				},
				{
					id: 'fa-convenience-hides-cost-fc-watch',
					role: 'watch_out',
					front: 'What is subscription creep?',
					back: 'Recurring charges stacking up after the original decision stops mattering.',
				},
				{
					id: 'fa-convenience-hides-cost-fc-use',
					role: 'use_it',
					front: 'What question should you ask?',
					back: 'Ask what spending has become automatic without still being important.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Audit automatic spending',
				prompt: 'List every recurring payment you can remember, then mark which ones still clearly earn their place.',
			},
			revision: 1,
		},
	],
};
